import { ChevronLeftIcon } from "@heroicons/react/24/outline";
import { workflowPayloadFromWorkflow } from "components/admin/tenant/workflow/workflow-edit";
import { SupportEmailLink } from "components/app/support-email-link";
import { ContentFull, ContentHeader } from "components/layout";
import { DeleteButton } from "components/ui/action-button";
import { Badge } from "components/ui/badge";
import { fancyConfirm } from "components/ui/fancy-confirm";
import { InlineBanner } from "components/ui/inline-banner";
import { PrimaryButton } from "components/ui/primary-button";
import { SecondaryButton } from "components/ui/secondary-button";
import {
    CustomListKey,
    GetWorkflowSettingsResult,
    useDeleteWorkflowMutation,
    useSetWorkflowSettingMutation,
    WorkflowStatus,
} from "generated/graphql";
import { useTaskNouns } from "hooks/use-task-nouns";
import React from "react";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { loadTenant, navigate } from "store/actions";
import { useProductName } from "store/selectors/branding";
import { useCustomList } from "store/selectors/bridge";
import { useCrmMode } from "store/selectors/hooks";
import { handleEvent } from "util/user-events";
import { EditableWorkflow } from "../types";
import {
    blueprintChangeEventProperties,
    WizardStepArrival,
    wizardStepViewEventProperties,
    workflowEventProperties,
} from "./builder-analytics";
import { useWorkflowEditor } from "./use-workflow-editor";
import { WizardBasics } from "./wizard-basics";
import { WizardReview } from "./wizard-review";
import { WizardStepper } from "./wizard-stepper";
import { WizardSteps } from "./wizard-steps";
import {
    applyBlueprint,
    BlueprintContext,
    blueprintResetNotice,
    BlueprintResult,
    detectBlueprint,
    sameBlueprint,
    WorkflowBlueprint,
} from "./workflow-blueprint";

const WIZARD_STEPS = ["Basics", "Steps", "Review"] as const;

interface WorkflowWizardProps {
    workflow: EditableWorkflow;
    workflowSettings: GetWorkflowSettingsResult;
    refetch: () => Promise<void>;
}

export const WorkflowWizard: React.FC<WorkflowWizardProps> = props => {
    const { workflow, workflowSettings, refetch } = props;

    const [stepIndex, setStepIndex] = React.useState(0);
    const [resetNotice, setResetNotice] = React.useState<string | null>(null);
    const applyInFlight = React.useRef(false);
    const productName = useProductName();
    const nouns = useTaskNouns();
    const { mode } = useCrmMode();
    const globalDispatch = useDispatch();
    const { readPayload, editPayload, saving } = useWorkflowEditor(workflow, refetch);
    const deleteWorkflowMutation = useDeleteWorkflowMutation();
    const setWorkflowSettingMutation = useSetWorkflowSettingMutation();
    const { customList: clientStatusCustomList } = useCustomList(
        CustomListKey.ClientStatus,
    );
    const { customList: homeCareLeadStatusCustomList } = useCustomList(
        CustomListKey.HomeCareLeadStatus,
    );

    const editable = !!workflow.regionVisibility?.editable;
    const isDraft = workflow.status === WorkflowStatus.Draft;

    const blueprintContext = React.useMemo<BlueprintContext>(
        () => ({
            nouns,
            clientStatusCustomList: clientStatusCustomList ?? null,
            homeCareLeadStatusCustomList: homeCareLeadStatusCustomList ?? null,
        }),
        [nouns, clientStatusCustomList, homeCareLeadStatusCustomList],
    );

    const currentPayload = React.useMemo(
        () => workflowPayloadFromWorkflow(workflow),
        [workflow],
    );
    const blueprint = React.useMemo(
        () => detectBlueprint(currentPayload),
        [currentPayload],
    );
    const canEditStructure = editable && isDraft && !!blueprint;
    const storedSettings = React.useMemo(
        () =>
            workflowSettings.tenant
                .filter(value => value.workflowId === workflow.id)
                .map(value => ({ key: value.key, textValue: value.textValue ?? "" })),
        [workflowSettings.tenant, workflow.id],
    );

    const eventProperties = React.useMemo(
        () => workflowEventProperties(workflow, blueprint),
        [workflow, blueprint],
    );

    const arrivalReported = React.useRef(false);

    React.useEffect(() => {
        if (arrivalReported.current) {
            return;
        }
        arrivalReported.current = true;
        handleEvent(
            "workflow-builder-step-view",
            wizardStepViewEventProperties(workflow, blueprint, {
                wizardStep: WIZARD_STEPS[0],
                arrivedBy: "open",
            }),
        );
    }, [workflow, blueprint]);

    const goToStep = React.useCallback(
        (index: number, arrivedBy: WizardStepArrival) => {
            setStepIndex(index);
            handleEvent(
                "workflow-builder-step-view",
                wizardStepViewEventProperties(workflow, blueprint, {
                    wizardStep: WIZARD_STEPS[index],
                    arrivedBy,
                }),
            );
        },
        [workflow, blueprint],
    );

    const handleStepperSelect = React.useCallback(
        (index: number) => goToStep(index, "stepper"),
        [goToStep],
    );

    const onChangeTrigger = React.useCallback(
        (newWorkflow: EditableWorkflow) => {
            const { triggerInput } = workflowPayloadFromWorkflow(newWorkflow);
            handleEvent("workflow-builder-edit", {
                ...eventProperties,
                field: "trigger",
            });
            editPayload(payload => ({ ...payload, triggerInput }));
        },
        [editPayload, eventProperties],
    );

    const onChangeAction = React.useCallback(
        (newWorkflow: EditableWorkflow) => {
            const { actionEntities, actorMode, defaultActorId } =
                workflowPayloadFromWorkflow(newWorkflow);
            handleEvent("workflow-builder-edit", {
                ...eventProperties,
                field: "action",
            });
            editPayload(payload => ({
                ...payload,
                actionEntities,
                actorMode,
                defaultActorId,
            }));
        },
        [editPayload, eventProperties],
    );

    const persistBlueprint = React.useCallback(
        async (result: BlueprintResult) => {
            try {
                await editPayload(() => result.payload);
                await Promise.all(
                    result.settingResets.map(reset =>
                        setWorkflowSettingMutation.mutateAsync({
                            params: {
                                workflowId: workflow.id,
                                key: reset.key,
                                textValue: reset.textValue,
                            },
                        }),
                    ),
                );
                if (result.settingResets.length > 0) {
                    loadTenant(globalDispatch);
                }
            } finally {
                refetch();
            }
        },
        [editPayload, setWorkflowSettingMutation, workflow.id, globalDispatch, refetch],
    );

    const handleApplyBlueprint = React.useCallback(
        async (target: WorkflowBlueprint) => {
            if (!blueprint || !editable || !isDraft || applyInFlight.current) {
                return;
            }
            if (sameBlueprint(blueprint, target)) {
                return;
            }
            const result = applyBlueprint(
                readPayload(),
                storedSettings,
                target,
                blueprintContext,
            );
            if (!result) {
                return;
            }
            applyInFlight.current = true;
            setResetNotice(
                blueprintResetNotice(
                    result,
                    blueprint.entityType,
                    target.entityType,
                    mode,
                ),
            );
            handleEvent("workflow-builder-blueprint-change", {
                ...blueprintChangeEventProperties(workflow.id, blueprint, target),
                settingsResetCount: result.settingResets.length,
            });
            try {
                await persistBlueprint(result);
            } finally {
                applyInFlight.current = false;
            }
        },
        [
            blueprint,
            editable,
            isDraft,
            readPayload,
            storedSettings,
            blueprintContext,
            mode,
            persistBlueprint,
            workflow.id,
        ],
    );

    const publishWithConfirmation = React.useCallback(
        async (source: "header" | "review") => {
            if (!editable || !isDraft) {
                return false;
            }
            const confirmed = await fancyConfirm(
                "Publish workflow?",
                "This workflow will go live and run whenever its trigger is met. You can unpublish it at any time.",
                "Yes, publish",
                "Cancel",
                { confirmStyle: "question" },
            );
            if (!confirmed) {
                return false;
            }
            await editPayload(payload => ({
                ...payload,
                status: WorkflowStatus.Published,
            }));
            handleEvent("workflow-builder-publish", { ...eventProperties, source });
            return true;
        },
        [editable, isDraft, editPayload, eventProperties],
    );

    const handlePublish = React.useCallback(
        async (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            await publishWithConfirmation("header");
        },
        [publishWithConfirmation],
    );

    const handlePublishAndFinish = React.useCallback(
        async (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            const published = await publishWithConfirmation("review");
            if (published) {
                navigate("/settings/workflow");
            }
        },
        [publishWithConfirmation],
    );

    const handleUnpublish = React.useCallback(
        async (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            if (!editable || isDraft) {
                return;
            }
            const confirmed = await fancyConfirm(
                "Unpublish workflow?",
                "This workflow will stop running for everyone. Anything it has already scheduled will be cancelled and will not run. You can publish it again at any time.",
                "Yes, unpublish",
                "Cancel",
            );
            if (!confirmed) {
                return;
            }
            await editPayload(payload => ({
                ...payload,
                status: WorkflowStatus.Draft,
            }));
            handleEvent("workflow-builder-unpublish", eventProperties);
        },
        [editable, isDraft, editPayload, eventProperties],
    );

    const handleDelete = React.useCallback(async () => {
        if (!editable) {
            return;
        }
        await deleteWorkflowMutation.mutateAsync({ workflowId: workflow.id });
        handleEvent("workflow-builder-delete", eventProperties);
        await refetch();
        navigate("/settings/workflow");
    }, [editable, deleteWorkflowMutation, workflow.id, refetch, eventProperties]);

    const stepProps = {
        workflow,
        workflowSettings,
        editable,
        isDraft,
        canEditStructure,
        onChangeTrigger,
        onChangeAction,
        editPayload,
        refetch,
        blueprint: {
            blueprint,
            context: blueprintContext,
            onApply: handleApplyBlueprint,
        },
    };

    return (
        <ContentFull>
            <div className="space-y-4">
                <div>
                    <Link
                        to="/settings/workflow"
                        className="text-sm text-gray-500 hover:text-gray-700 inline-flex items-center gap-1">
                        <ChevronLeftIcon className="w-4 h-4" /> All workflows
                    </Link>
                    <div className="flex justify-between items-center mt-1">
                        <ContentHeader>
                            <span className="inline-flex items-center gap-2">
                                {workflow.name}
                                {isDraft ? (
                                    <Badge value="Draft" type="info" />
                                ) : (
                                    <Badge value="Active" type="success" />
                                )}
                            </span>
                        </ContentHeader>
                        {editable ? (
                            <div className="flex items-center gap-2">
                                {isDraft ? (
                                    <SecondaryButton
                                        onClick={handlePublish}
                                        disabled={saving}>
                                        Publish
                                    </SecondaryButton>
                                ) : (
                                    <SecondaryButton
                                        onClick={handleUnpublish}
                                        disabled={saving}>
                                        Unpublish
                                    </SecondaryButton>
                                )}
                                <DeleteButton
                                    backgroundColor="bg-white"
                                    confirm={{
                                        title: "Delete workflow?",
                                        message:
                                            "Are you sure you want to delete this workflow? Any actions this workflow has already scheduled will be cancelled and will not run.",
                                    }}
                                    onClick={handleDelete}
                                />
                            </div>
                        ) : null}
                    </div>
                </div>
                {!editable ? (
                    <InlineBanner type="info">
                        This workflow requires administrative access to additional{" "}
                        {productName} regions to make changes. Contact your agency
                        administrator or <SupportEmailLink /> to request changes.
                    </InlineBanner>
                ) : null}
                {resetNotice ? (
                    <InlineBanner type="info">{resetNotice}</InlineBanner>
                ) : null}
                <div className="max-w-3xl">
                    <WizardStepper
                        steps={WIZARD_STEPS}
                        currentIndex={stepIndex}
                        onSelect={handleStepperSelect}
                    />
                </div>
                <div className="max-w-3xl">
                    {stepIndex === 0 ? <WizardBasics {...stepProps} /> : null}
                    {stepIndex === 1 ? <WizardSteps {...stepProps} /> : null}
                    {stepIndex === 2 ? <WizardReview {...stepProps} /> : null}
                </div>
                <div className="max-w-3xl flex items-center justify-between">
                    {stepIndex === 0 ? (
                        <Link to="/settings/workflow">
                            <SecondaryButton>Back to workflows</SecondaryButton>
                        </Link>
                    ) : (
                        <SecondaryButton onClick={() => goToStep(stepIndex - 1, "back")}>
                            Back
                        </SecondaryButton>
                    )}
                    {stepIndex < WIZARD_STEPS.length - 1 ? (
                        <PrimaryButton
                            onClick={() => goToStep(stepIndex + 1, "continue")}>
                            Continue &rarr;
                        </PrimaryButton>
                    ) : isDraft && editable ? (
                        <div className="flex items-center gap-2">
                            <Link to="/settings/workflow">
                                <SecondaryButton>Finish later</SecondaryButton>
                            </Link>
                            <PrimaryButton
                                onClick={handlePublishAndFinish}
                                disabled={saving}>
                                Publish workflow
                            </PrimaryButton>
                        </div>
                    ) : (
                        <Link to="/settings/workflow">
                            <PrimaryButton>Done</PrimaryButton>
                        </Link>
                    )}
                </div>
            </div>
        </ContentFull>
    );
};
